/**
 * User Data Helpers for Unified Authentication
 * 
 * Single token key ('token') for all users (facilitators and participants).
 * User context (roles, programs) is fetched from /api/auth/me endpoint.
 */

// Storage keys - unified
const STORAGE_KEYS = {
  TOKEN: 'token',
  USER_DATA: 'userData',
  PENDING_CHECK_INS: 'pendingCheckIns',
}

// Legacy keys for migration
const LEGACY_KEYS = {
  USER_TOKEN: 'userToken',
  COHORT_TOKEN: 'cohortSignupToken',
  USER_ID: 'userId',
  COHORT_ID: 'cohortSignupId',
  COHORT_DATA: 'cohortSignupData',
  LEGACY_PENDING: 'pendingCohortCheckIns',
}

/**
 * Migrate legacy tokens to unified token key
 * Call this on app startup
 */
export const migrateStorage = () => {
  const currentToken = localStorage.getItem(STORAGE_KEYS.TOKEN)
  
  // If we already have a unified token, skip migration
  if (currentToken) return
  
  // Try to migrate from legacy keys
  const legacyToken = 
    localStorage.getItem(LEGACY_KEYS.USER_TOKEN) ||
    localStorage.getItem(LEGACY_KEYS.COHORT_TOKEN)
  
  if (legacyToken) {
    localStorage.setItem(STORAGE_KEYS.TOKEN, legacyToken)
  }
  
  // Clean up legacy keys
  Object.values(LEGACY_KEYS).forEach(key => {
    localStorage.removeItem(key)
  })
}

/**
 * Get auth token from localStorage
 */
export const getToken = () => {
  return localStorage.getItem(STORAGE_KEYS.TOKEN)
}

/**
 * Alias for backward compatibility
 */
export const getUserToken = getToken

/**
 * Save auth token
 * @param {string} token - JWT token
 */
export const saveToken = (token) => {
  localStorage.setItem(STORAGE_KEYS.TOKEN, token)
}

/**
 * Get cached user data from localStorage
 */
export const getUserData = () => {
  const data = localStorage.getItem(STORAGE_KEYS.USER_DATA)
  if (data) {
    try {
      return JSON.parse(data)
    } catch (e) {
      console.error('Error parsing user data:', e)
    }
  }
  return null
}

/**
 * Save user data to localStorage (cache from /api/auth/me)
 * @param {object} userData - User data from API
 */
export const saveUserData = (userData) => {
  localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData))
}

/**
 * Save auth data (token + user data)
 * @param {string} token - JWT token
 * @param {object} userData - User data
 */
export const saveAuth = (token, userData) => {
  saveToken(token)
  if (userData) {
    saveUserData(userData)
  }
}

/**
 * Clear all auth data (logout)
 */
export const clearAuth = () => {
  localStorage.removeItem(STORAGE_KEYS.TOKEN)
  localStorage.removeItem(STORAGE_KEYS.USER_DATA)
  localStorage.removeItem(STORAGE_KEYS.PENDING_CHECK_INS)
  
  // Also clear any remaining legacy keys
  Object.values(LEGACY_KEYS).forEach(key => {
    localStorage.removeItem(key)
  })
}

/**
 * Alias for backward compatibility
 */
export const clearUserData = clearAuth

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!getToken()
}

/**
 * Get user from cached data
 */
export const getUser = () => {
  const userData = getUserData()
  if (!userData) return null
  
  // Support both new format (flat) and old format (nested)
  if (userData.user) {
    return userData.user
  }
  return userData
}

/**
 * Get user ID from cached data
 */
export const getUserId = () => {
  const user = getUser()
  return user?.id || null
}

/**
 * Check if cached user is a facilitator
 */
export const isFacilitator = () => {
  const userData = getUserData()
  return userData?.isFacilitator || false
}

/**
 * Get enrolled programs from cached data
 */
export const getEnrolledPrograms = () => {
  const userData = getUserData()
  return userData?.enrolledPrograms || []
}

/**
 * Check if user is enrolled in a specific program
 */
export const isEnrolledInProgram = (programSlug) => {
  const programs = getEnrolledPrograms()
  return programs.some(p => p.programSlug === programSlug)
}

/**
 * Get pending check-ins
 */
export const getPendingCheckIns = () => {
  const data = localStorage.getItem(STORAGE_KEYS.PENDING_CHECK_INS)
  return data ? JSON.parse(data) : []
}

/**
 * Save pending check-ins
 */
export const savePendingCheckIns = (checkIns) => {
  localStorage.setItem(STORAGE_KEYS.PENDING_CHECK_INS, JSON.stringify(checkIns))
}

/**
 * Clear pending check-ins
 */
export const clearPendingCheckIns = () => {
  localStorage.removeItem(STORAGE_KEYS.PENDING_CHECK_INS)
}

/**
 * Add a pending check-in
 */
export const addPendingCheckIn = (checkIn) => {
  const pending = getPendingCheckIns()
  pending.push(checkIn)
  savePendingCheckIns(pending)
}

// Export storage keys for components that need direct access
export { STORAGE_KEYS }
